// Cliente de la API (handoff §5–§6). Un único POST al final con reintentos y
// backoff; el estado local nunca se descarta ante fallos de red.
import { INSTRUMENT_VERSION, pruneAnswers, type Answers } from '@gbcmc/survey-core';

const API_BASE = import.meta.env.VITE_API_BASE ?? '/api';

// Modo demo: simula el backend en el propio cliente (sin red), para publicar
// un demo estático revisable antes de tener la API real (F3). Se activa en el
// build `--mode demo`. NO afecta al build de producción.
const DEMO = import.meta.env.VITE_DEMO === 'true';

export interface SurveyConfig {
  open: boolean;
  period: string;
}

export interface SubmitResult {
  id: string;
}

/** La ventana de medición está cerrada (HTTP 403). */
export class SurveyClosedError extends Error {
  constructor() {
    super('La medición está cerrada.');
    this.name = 'SurveyClosedError';
  }
}

/** El backend rechazó el payload (HTTP 400). No se reintenta. */
export class ValidationError extends Error {
  constructor() {
    super('El servidor rechazó las respuestas.');
    this.name = 'ValidationError';
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

/** Estado de la ventana de medición. Si falla, el llamador decide (fail-open). */
export async function getConfig(): Promise<SurveyConfig> {
  if (DEMO) return { open: true, period: '2026-1' };
  const res = await fetch(`${API_BASE}/config`, { headers: { accept: 'application/json' } });
  if (!res.ok) throw new Error(`config HTTP ${res.status}`);
  return (await res.json()) as SurveyConfig;
}

/**
 * Envía las respuestas. Poda las huérfanas de la ramificación antes de enviar.
 * Reintenta hasta 3 veces (backoff) ante fallos de red/servidor; propaga
 * inmediatamente los errores no recuperables (cerrada / validación).
 */
export async function submitResponses(
  answers: Answers,
  options: { period?: string; honeypot?: string; retries?: number } = {},
): Promise<SubmitResult> {
  const { period = '2026-1', honeypot = '', retries = 2 } = options;
  const payload = {
    version: INSTRUMENT_VERSION,
    period,
    honeypot,
    answers: pruneAnswers(answers),
  };

  if (DEMO) {
    // Simula el guardado (ejercita la poda real, pero sin red ni persistencia).
    await sleep(900);
    return { id: `demo-${Date.now()}` };
  }

  let attempt = 0;
  let lastError: unknown;
  for (;;) {
    try {
      const res = await fetch(`${API_BASE}/responses`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (res.status === 403) throw new SurveyClosedError();
      if (res.status === 400) throw new ValidationError();
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      return (await res.json()) as SubmitResult;
    } catch (err) {
      if (err instanceof SurveyClosedError || err instanceof ValidationError) throw err;
      lastError = err;
      if (attempt >= retries) throw lastError;
      await sleep(600 * Math.pow(2, attempt)); // 600ms, 1200ms…
      attempt += 1;
    }
  }
}
